import React, { useEffect, useMemo, useRef, useState } from 'react';

import useWindowSize from '@editor/hooks/useWindowSize';
import { useOnOutsideClick } from '@repo/react-hooks/useOnOutsideClick';
import { Spacer } from '@repo/ui/components/Spacer';
import { DrawerTheme } from 'node_modules/@repo/ui/dist/components/Drawer/types';
import { CSSTransition } from 'react-transition-group';
import { useTheme } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import { Divider, DividerWrapper } from '../utilities/styles';

import DropdownOption from './DropdownOption';
import { DropdownWrapper, GroupTitle } from './styles';
import { DropdownOptionCategory, DropdownProps, Option } from './types';

const getPositionStyles = (
  dropdown: HTMLDivElement,
  offsetParent?: Element | null,
  relativeParent?: Element | null,
  minSafeMargin = 20
): [rightAligned: boolean, isOnTop: boolean, maxHeight: number | undefined] => {
  const dropdownRect = dropdown.getBoundingClientRect();
  const { right, bottom } = dropdownRect;
  if (!offsetParent || !relativeParent) {
    return [
      right + minSafeMargin > window.innerWidth,
      bottom + minSafeMargin > window.innerHeight,
      undefined,
    ];
  }

  const offsetParentRect = offsetParent.getBoundingClientRect();
  const relativeParentRect = relativeParent.getBoundingClientRect();

  const topPlace = Math.abs(offsetParentRect.top - relativeParentRect.top);
  const bottomPlace = Math.abs(
    offsetParentRect.bottom - relativeParentRect.bottom
  );

  return [
    right + minSafeMargin > offsetParentRect.right,
    topPlace > bottomPlace,
    Math.max(topPlace, bottomPlace) - minSafeMargin,
  ];
};

const Dropdown: React.FC<DropdownProps> = ({
  options,
  optionCustomStyles,
  small = false,
  compact = false,
  expanded,
  activeOption,
  onOptionClick,
  onMouseEnterOption,
  onMouseLeaveOption,
  customStyles,
  dataTestId,
  onPointerDownAway,
  renderItem,
  offsetParent,
  relativeParent,
  forceBottomDropdown,
  searchText,
  minSafeMargin,
  activeOptions,
}) => {
  const nodeRef = useRef<null | HTMLDivElement>(null);
  const [rightAligned, setRightAligned] = useState<boolean>(false);
  const [isOnTop, setIsOnTop] = useState<boolean>(false);
  const [maxHeight, setMaxHeight] = useState<number>(compact ? 124 : 300);
  const { isMobile } = useWindowSize();
  const theme = useTheme() as DrawerTheme;
  useOnOutsideClick(nodeRef, onPointerDownAway);

  useEffect(() => {
    if (!nodeRef.current || !expanded) {
      return;
    }

    const [rightAligned, isOnTop, maxCalcHeight] = getPositionStyles(
      nodeRef.current,
      offsetParent,
      relativeParent,
      minSafeMargin
    );

    setRightAligned(rightAligned);
    setIsOnTop(isOnTop);
    if (maxCalcHeight) {
      setMaxHeight(Math.min(maxCalcHeight, maxHeight));
    }
  }, [
    expanded,
    offsetParent,
    compact,
    relativeParent,
    maxHeight,
    minSafeMargin,
  ]);

  useEffect(() => {
    if (!nodeRef.current || !expanded || !searchText) {
      return;
    }

    const matchingItemIndex = options.findIndex(({ label }) =>
      label.toLowerCase().startsWith(searchText.toLowerCase())
    );
    if (matchingItemIndex === -1) {
      return;
    }

    nodeRef.current.scrollTo(
      0,
      nodeRef.current.scrollHeight * (matchingItemIndex / options.length)
    );
  }, [options, expanded, searchText]);

  const renderOptionMenu = useMemo(() => {
    const getOptionMenu = (): React.ReactNode => {
      return options.map((option: Option) => {
        const isActive = activeOptions?.length
          ? activeOptions.some((activeOption) => activeOption.id === option.id)
          : activeOption?.id === option.id;
        // to avoid React two children with the same key warning
        const keyId = uuidv4();
        /** Render group title or divider in the dropdown list */
        if (option.category !== DropdownOptionCategory.Option) {
          if (option.category === DropdownOptionCategory.GroupTitle) {
            return <GroupTitle key={keyId}>{option.label}</GroupTitle>;
          }
          if (option.category === DropdownOptionCategory.Divider) {
            return (
              <DividerWrapper key={keyId}>
                <Spacer height="4px" />
                <Divider />
                <Spacer height="4px" />
              </DividerWrapper>
            );
          }
        }

        return (
          <DropdownOption
            key={keyId}
            compact={compact}
            isActive={isActive}
            option={option}
            optionCustomStyles={optionCustomStyles}
            renderItem={renderItem}
            small={small}
            onClick={onOptionClick}
            onMouseEnter={onMouseEnterOption}
            onMouseLeave={onMouseLeaveOption}
          />
        );
      });
    };
    return getOptionMenu();
  }, [
    activeOption?.id,
    activeOptions,
    compact,
    onMouseEnterOption,
    onMouseLeaveOption,
    onOptionClick,
    optionCustomStyles,
    options,
    renderItem,
    small,
  ]);

  return (
    <CSSTransition unmountOnExit in={expanded} nodeRef={nodeRef} timeout={300}>
      <DropdownWrapper
        ref={nodeRef}
        compact={compact}
        customStyles={customStyles}
        data-testid={dataTestId}
        isOnTop={!forceBottomDropdown && isOnTop}
        maxHeight={isMobile && theme.isHalfOpened ? 200 : maxHeight}
        rightAligned={rightAligned}
        small={small}
      >
        {renderOptionMenu}
      </DropdownWrapper>
    </CSSTransition>
  );
};

export default React.memo(Dropdown);
